/**
 *
 * QuantitySelector
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useFormatMessage } from '@artemis/integrations/contentful/utils';

const Container = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 10px;
  }
`;

const Sign = styled.button`
  background-color: ${props => props.theme.palette.grey[200]};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  height: 48px;
  width: 48px;
  padding: 0;
  cursor: pointer;
  user-select: none;
  border: none;
  transition: 0.25s background-color ease;
  ${({ theme }) => theme.isTablet`
    height: 38px;
    width: 38px;
  `};

  ${props =>
    props.error &&
    `
      opacity: 25%;
    `};

  &:hover {
    background-color: ${({ theme }) =>
      theme.palette.hover.onLightBg(theme.palette.grey[200])};
  }
`;

const Quantity = styled.div``;

const ScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * Component for Quantity Selector
 */
const QuantitySelector = ({
  onDecrease,
  onIncrease,
  quantity,
  error,
  itemName,
}) => {
  const decreaseLabel = useFormatMessage({
    entry: 'quantitySelector.menu.decreaseQuantityButton',
  });
  const increaseLabel = useFormatMessage({
    entry: 'quantitySelector.menu.increaseQuantityButton',
  });
  const quantityLabel = useFormatMessage({
    entry: 'quantitySelector.menu.quantityLabel',
    values: { quantity, itemName },
  });
  const contentUpdateWarning = useFormatMessage({
    entry: 'global.contentUpdateWarning',
  });

  return (
    <Container>
      <ScreenReaderOnly>{contentUpdateWarning}</ScreenReaderOnly>
      <Sign onClick={onDecrease} data-testid="minus" aria-label={decreaseLabel}>
        -
      </Sign>
      <Quantity aria-hidden="true">{quantity}</Quantity>
      <ScreenReaderOnly aria-live="polite">{quantityLabel}</ScreenReaderOnly>
      <Sign
        onClick={onIncrease}
        data-testid="plus"
        error={error}
        aria-label={increaseLabel}
      >
        +
      </Sign>
    </Container>
  );
};

QuantitySelector.propTypes = {
  onDecrease: PropTypes.func,
  onIncrease: PropTypes.func,
  quantity: PropTypes.number,
  error: PropTypes.bool,
  itemName: PropTypes.string,
};

QuantitySelector.defaultProps = {
  quantity: 0,
};

export default QuantitySelector;
